import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FoxyMark } from './guide/FoxyMark';
import { ColorMark } from './IntegrationLogos';
import { useStage } from './guide/stage';
import { FileText, Globe, Lightbulb, ShieldBan, Users, Workflow } from 'lucide-react';
import { useMedia } from '@/hooks/useMedia';
import { logContent } from './guide/beats';
import './infrastructure.css';

// Owner copy (2026-10-16). Every node is backed in code: recorded flows + validator, PKB documents
// and never-say list, live voice/browser sessions, site chat, walkthroughs, briefs and reviewed
// follow-ups, Salesforce/HubSpot/Zoho/Slack/Teams, insights. Jira and Linear are direction.
// Composition (owner, 2026-10-16): a 5-column grid 1fr · 72px · 420px · 72px · 1fr with the expert node on
// the container's axis; three input nodes and three output nodes of equal height, vertically centred
// on the node, mirroring each other; the right connectors are the left ones mirrored about the axis.
type Chip = [string, boolean];
type NodeDef = [typeof Globe, string, Chip[]?];
const inputs: NodeDef[] = [
  [Workflow, 'Product workflows · recorded on the real app'],
  [FileText, 'Documents and answers'],
  [ShieldBan, 'Boundaries and a never-say list'],
];
// three outputs mirror the three inputs: ACTS ON and RETURNS TO merged into one labelled group
const outputs: NodeDef[] = [
  [Globe, 'Your website & live sessions'],
  [Users, 'Your teams: Slack, Teams, CRM', [['Salesforce', false], ['HubSpot', false], ['Zoho', false], ['Slack', false], ['Teams', false]]],
  [Lightbulb, 'Product: questions and friction', [['Jira · direction', true], ['Linear · direction', true]]],
];

type Wire = { d: string; id: string };

export function InfrastructureSection() {
  const grid = useRef<HTMLDivElement>(null);
  const brain = useRef<HTMLDivElement>(null);
  const inRefs = useRef<(HTMLElement | null)[]>([]);
  const outRefs = useRef<(HTMLElement | null)[]>([]);
  const desktop = useMedia('(min-width: 1000px)');
  const reduced = useMedia('(prefers-reduced-motion: reduce)');
  const [wires, setWires] = useState<Wire[]>([]);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const stage = useStage();
  // Connector dots (2.6s loop): dots flowing INTO the node put the mark in thinking (processing), dots
  // flowing OUT put it in speaking with a gentle level (B16: Mr. Clippy follows them in, then out).
  const [markState, setMarkState] = useState<'listening' | 'thinking' | 'speaking'>('listening');
  const markLevel = useRef(0.3);
  // the loop (dots, the mark's states, the pulses) runs only while the section is ≥ 40% visible: nothing animates off-screen
  const [active, setActive] = useState(false);
  useEffect(() => {
    const el = grid.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setActive(e.isIntersecting && e.intersectionRatio >= 0.4), { threshold: [0, 0.4] });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!desktop || reduced || !active) { setMarkState('listening'); return; }
    let phase = 0;
    const id = window.setInterval(() => {
      phase = (phase + 1) % 2;
      setMarkState(phase === 0 ? 'thinking' : 'speaking');
      stage?.react('.fx-infra', 'clippy', { lookAt: phase === 0 ? inRefs.current[1] : outRefs.current[1] });
    }, 1300);
    return () => window.clearInterval(id);
  }, [active, desktop, reduced, stage]);

  // B16 (content): when a dot reaches an output node, that node's hairline pulses ember once (280ms); the Product
  // node's pulse is his own signal coming back, so he nods once. The dots are SMIL: `repeatEvent` fires each time an
  // outward dot completes its path, i.e. arrives at the node.
  useEffect(() => {
    if (!desktop || reduced || !active || !grid.current) return;
    const anims = Array.from(grid.current.querySelectorAll<SVGElement>('animateMotion[data-out]'));
    if (!anims.length) return;
    const pulse = (i: number) => {
      const node = outRefs.current[i];
      if (!node) return;
      logContent('C16', 'output node pulses as a dot arrives');
      node.classList.remove('pulse');
      void node.offsetWidth;
      node.classList.add('pulse');
      window.setTimeout(() => node.classList.remove('pulse'), 300);
      if (i === 2) stage?.react('.fx-infra', 'clippy', { nod: true });
    };
    const handlers = anims.map((a) => { const i = +(a.dataset.out ?? 0); const h = () => pulse(i); a.addEventListener('repeatEvent', h); return [a, h] as const; });
    return () => handlers.forEach(([a, h]) => a.removeEventListener('repeatEvent', h));
  }, [active, desktop, reduced, stage, wires]);

  // Connectors follow the nodes' boxes at any width.
  useLayoutEffect(() => {
    const el = grid.current;
    if (!el || !desktop) {
      setWires([]);
      return;
    }
    const measure = () => {
      const g = el.getBoundingClientRect();
      const b = brain.current?.getBoundingClientRect();
      if (!b) return;
      const rel = (r: DOMRect) => ({ left: r.left - g.left, right: r.right - g.left, top: r.top - g.top, mid: r.top - g.top + r.height / 2 });
      const bb = rel(b);
      const axis = g.width / 2;
      const next: Wire[] = [];
      const ins = inRefs.current.filter(Boolean) as HTMLElement[];
      ins.forEach((n, i) => {
        const r = rel(n.getBoundingClientRect());
        const x1 = r.right, y1 = r.mid, x2 = bb.left, y2 = bb.mid + (i - 1) * 14;
        const dx = (x2 - x1) / 2;
        next.push({ id: `in-${i}`, d: `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}` });
        // the mirror: every x reflected about the axis, dots flowing out
        next.push({ id: `out-${i}`, d: `M ${2 * axis - x2} ${y2} C ${2 * axis - (x2 - dx)} ${y2}, ${2 * axis - (x1 + dx)} ${y1}, ${2 * axis - x1} ${y1}` });
      });
      setWires(next);
      setSize({ w: g.width, h: g.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [desktop]);

  const node = (Icon: typeof Globe, text: string, ref: (el: HTMLElement | null) => void, chips?: Chip[]) => (
    <li className="fx-node" ref={ref} key={text}>
      <Icon size={16} aria-hidden="true" />
      <span>
        {text}
        {chips && (
          <span className="fx-node-chips">
            {chips.map(([c, dir]) => (
              // smaller than the ledger-card default: the "Your teams" node packs five marks into
              // a fixed 104px-tall card (two rows) — 20px overflowed it and hid Teams behind the
              // next card (measured, 2026-10-23)
              <ColorMark name={c.replace(/ · direction$/, '')} dir={dir} key={c} size={14} />
            ))}
          </span>
        )}
      </span>
    </li>
  );

  return (
    <section className="fx-infra fx-rv" id="infrastructure">
      <div className="container">
        <p className="fx-eyebrow">Infrastructure</p>
        <h2>Infrastructure, not a chatbot.</h2>
        <p className="fx-infra-sub">
          One brain built from your product, acting wherever your buyers and
          users are, returning what your teams need.
        </p>
        <div className="fx-diagram" ref={grid}>
          {desktop && size.w > 0 && (
            <svg className="fx-wires" width={size.w} height={size.h} viewBox={`0 0 ${size.w} ${size.h}`} aria-hidden="true">
              {wires.map((wire, i) => (
                <g key={wire.id}>
                  <path id={`fx-wire-${wire.id}`} d={wire.d} />
                  {!reduced && active && (
                    <circle r="2.5" className="fx-wire-dot">
                      <animateMotion dur="2.6s" repeatCount="indefinite" begin={`${i * 0.3}s`} data-out={wire.id.startsWith('out-') ? wire.id.slice(4) : undefined}>
                        <mpath href={`#fx-wire-${wire.id}`} />
                      </animateMotion>
                    </circle>
                  )}
                </g>
              ))}
            </svg>
          )}
          <div className="fx-group fx-inputs">
            <span className="fx-group-label">Inputs</span>
            <ul>
              {inputs.map(([Icon, text], i) =>
                node(Icon, text, (el) => {
                  inRefs.current[i] = el;
                }),
              )}
            </ul>
          </div>
          <div className="fx-brain" ref={brain}>
            <div className="fx-brain-left">
              <FoxyMark size={56} state={markState} levelRef={markLevel} />
              <h3>One expert</h3>
              <ul>
                <li>replay-verified nightly</li>
                <li>narrates when stale</li>
                <li>multi-lingual</li>
              </ul>
            </div>
            <div className="fx-brain-live" aria-label="Live state: voice over WebRTC, a real Chromium browser, guardrails">
              <div className="fx-listen">
                <span className="fx-bars" aria-hidden="true">
                  {Array.from({ length: 14 }, (_, i) => (
                    <i key={i} style={{ '--i': i } as React.CSSProperties} />
                  ))}
                </span>
                <span>listening · live</span>
              </div>
              <dl>
                <div>
                  <dt>voice</dt>
                  <dd>real-time, WebRTC</dd>
                </div>
                <div>
                  <dt>browser</dt>
                  <dd>real Chromium, your app</dd>
                </div>
                <div>
                  <dt>guardrails</dt>
                  <dd>never-say list, output check</dd>
                </div>
              </dl>
            </div>
          </div>
          <div className="fx-group fx-outputs">
            <span className="fx-group-label">Outputs</span>
            <ul>
              {outputs.map(([Icon, text, chips], i) =>
                node(Icon, text, (el) => {
                  outRefs.current[i] = el;
                }, chips),
              )}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
